<template>
  <div class="create-contact">
    <add-contact-button
      :is-loading="isContactsLoading"
      :show-label="showLabel"
      :is-button-visible="true"
      @openForm="onOpenForm"
    />
    <transition name="contact-form">
      <div class="contact-form-modal" v-if="contactFormOpened">
        <div class="contact-form-modal-content">
          <loading-logo :autoplay="true" ref="loading" v-if="isLoading"/>
          <template v-if="!isLoading && !currentContact">
            <div class="close-form">
              <img src="../../assets/icon_clear.svg" @click="closeContactForm">
            </div>
            <create-contact-option :current-option="currentOption" @optionChosen="onChooseOption"/>
            <form class="create-contact-form" @submit.prevent="onSave">
              <div class="form-container">
                <vi-phone-number
                  v-if="currentOption === 'sms' || currentOption === 'whatsapp'"
                  v-model="contactModel.phoneNumber"
                  :autofocus="true"
                />
                <div class="error-msg"
                     v-if="!$v.contactModel.phoneNumber.phoneNumber && $v.contactModel.phoneNumber.$dirty">
                  Enter a valid phone number
                </div>
                <vi-input
                  v-if="currentOption === 'email'"
                  v-model="contactModel.email"
                  label="Email"
                  type="email"
                  :autofocus="true"
                />
                <div class="error-msg"
                     v-if="(!$v.contactModel.email.email || !$v.contactModel.email.required) && $v.contactModel.email.$dirty">
                  Enter a valid email
                </div>
                <vi-input
                  v-model="contactModel.name"
                  label="Name"
                  :autofocus="currentOption === 'link'"
                />
                <div class="error-msg" v-if="!$v.contactModel.name.required && $v.contactModel.name.$dirty">
                  Name cannot be blank
                </div>
                <div class="auto-connect">
                  <label>
                    <input type="checkbox" v-model="shouldAutoConnect"/>
                    Auto connect to the session
                  </label>
                </div>
                <div class="auto-connect" v-if="shouldAutoConnect">
                  <label>
                    <input type="checkbox" v-model="shouldRecord"/>
                    Record session
                  </label>
                </div>
                <div class="error-msg" v-if="errorMsg">
                  An error has occurred. Try again.
                </div>

                <vi-button :disabled="isLoading" :label="createContactLabel"/>
              </div>
            </form>
          </template>
          <template v-if="!isLoading && currentContact">
            <div class="close-form">
              <img src="../../assets/icon_clear.svg" @click="closeContactForm">
            </div>
            <div class="copy-contact">
              <div>Copy and send the link to the customer</div>
              <div>{{currentContact.link}}</div>
              <vi-button label="Copy link" @click="copyToClipBoard"/>
              <div v-if="autoConnectWaiting">You will be connected to the call after closing this window</div>
            </div>
          </template>
        </div>
      </div>

    </transition>
    <div
      ref="loading"
      class="loading-animation"
      :class="{'is-loading': isLoading}"
    >
    </div>
  </div>
</template>

<script>
  import AddContactButton from "./AddContactButton";
  import ViInput from "../ui/ViInput";
  import ViButton from "../ui/ViButton";
  import {email, required, requiredIf} from 'vuelidate/lib/validators'
  import lottie from 'lottie-web'
  import ViCountryCodePicker from "../ui/ViCountryCodePicker";
  import ViPhoneNumber from "../ui/ViPhoneNumber";
  import CreateContactOption from "./CreateContactOption";
  import CurrentOption from "./CurrentOption";
  import LoadingLogo from "../LoadingLogo";

  export default {
    name: 'CreateContactForm',
    components: {
      LoadingLogo,
      CurrentOption,
      CreateContactOption, ViPhoneNumber, ViCountryCodePicker, ViButton, ViInput, AddContactButton
    },
    props: {
      showLabel: {
        type: Boolean,
        default: true
      },
      isContactsLoading: {
        type: Boolean
      }
    },

    data: () => ({
      contactFormOpened: false,
      isLoading: false,
      countryCode: 'US',
      contactModel: {
        phoneNumber: null,
        name: null,
      },
      errorMsg: false,
      currentOption: null,
      currentContact: null,
      shouldAutoConnect: true,
      shouldRecord: false,
      autoConnectWaiting: null
    }),

    computed: {
      createContactLabel() {
        switch (this.currentOption) {
          case 'link':
            return 'Generate link';
          case 'sms':
            return 'Send link';
          case 'email':
            return 'Send email';
          case 'whatsapp':
            return 'Send WhatsApp message';
          default:
            return 'Generate Link'
        }
      }
    },

    validations: {
      contactModel: {
        phoneNumber: {
          required: requiredIf(function () {
            return this.currentOption === 'sms' || this.currentOption === 'whatsapp'
          }),
          phoneNumber(val) {
            return (this.currentOption === 'sms' || this.currentOption === 'whatsapp') ? /^(\+[1-9][0-9]{10}|\+[1-9][0-9]{11})$/g.test(val) : true;
          }
        },
        email: {
          email,
          required: requiredIf(function () {
            return this.currentOption === 'email'
          }),
        },
        name: {required}
      }
    },

    methods: {
      onOpenForm() {
        this.contactFormOpened = true;
        this.currentOption = 'sms'
        this.currentContact = null;
        this.shouldAutoConnect = true;
        this.autoConnectWaiting = null;
      },

      onSave() {
        this.$v.$touch();
        if (this.$v.$error) {
          return
        }

        this.isLoading = true;
        this.errorMsg = null;

        if (this.shouldAutoConnect) {
          this.saveContactAndAutoConnect()
        } else {
          this.saveContact()
        }
      },

      saveContact() {
        this.$store.dispatch('contacts/create', {...this.contactModel, type: this.currentOption})
          .then((contact) => {
            this.isLoading = false;
            if (this.currentOption === 'link') {
              this.currentContact = contact
            } else {
              this.closeContactForm()
            }
          })
          .catch(() => {
            this.isLoading = false;
            this.errorMsg = true
          })
      },

      saveContactAndAutoConnect() {
        this.$store.dispatch('contacts/createAndAutoConnect', {...this.contactModel, type: this.currentOption, shouldRecord: this.shouldRecord})
          .then((autoConnect) => {
            this.isLoading = false;
            if (this.currentOption === 'link') {
              this.currentContact = autoConnect.contact
              this.autoConnectWaiting = autoConnect
            } else {
              this.closeContactForm()
              this.autoConnectWaiting = null
              this.$emit('autoConnect', {...autoConnect, shouldRecord: this.shouldRecord})
            }

          })
          .catch(() => {
            this.isLoading = false;
            this.errorMsg = true
          })
      },

      setIsLoading(isLoading) {
        this.isLoading = isLoading;
        if (isLoading) {
          this.$nextTick()
            .then(() => {
              this.animation.goToAndPlay(0, true)
            })
        } else {
          this.$nextTick()
            .then(() => {
              this.animation.goToAndStop(0, true)
            })
        }
      },
      closeContactForm() {
        this.contactFormOpened = false;
        this.isLoading = false;
        this.contactModel = {
          phoneNumber: null,
          name: null
        };
        this.currentOption = null;
        this.currentContact = null;
        this.$v.$reset();
        if(this.autoConnectWaiting) {
          this.$emit('autoConnect', {...this.autoConnectWaiting, shouldRecord: this.shouldRecord})
        }
      },
      closeOnEscape(e) {
        if (e.which === 27) {
          this.closeContactForm()
        }
      },

      onChooseOption(option) {
        this.currentOption = option
      },

      onBack() {
        this.currentOption = false
      },

      copyToClipBoard() {
        let el = document.createElement('textarea');
        el.value = this.currentContact.link;
        el.setAttribute('readonly', '');
        el.style = {position: 'absolute', left: '-9999px'};
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      },
    },
    mounted() {
      this.animation = lottie.loadAnimation({
        container: this.$refs.loading,
        animationData: require('../../animations/animation_connecting'),
        loop: true,
        renderer: 'svg',
      });

      document.addEventListener('keydown', this.closeOnEscape, false)
    },

    beforeDestroy() {
      document.removeEventListener('keydown', this.closeOnEscape, false)
    }
  }
</script>

<style lang="scss">
  @import "CreateContactForm";
</style>
